import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { loadDotLottie, preloadDotLottie } from "@/lib/loadDotLottie";
import { DotLottieCanvas } from "@/components/DotLottieCanvas";
import type { HomeCta, HomeGrowthData } from "@/lib/homepage";

type GrowthProps = {
  data: HomeGrowthData;
};

function isExternalUrl(url: string) {
  return /^(https?:)?\/\//i.test(url) || url.startsWith("mailto:") || url.startsWith("tel:");
}

function CtaButton({ cta }: { cta?: HomeCta | null }) {
  if (!cta) return null;
  if (isExternalUrl(cta.url)) {
    return (
      <Button
        asChild
        className="bg-onplexus-gold text-onplexus-dark hover:bg-onplexus-gold-bright rounded-xl px-6 py-3 h-12 font-semibold text-lg"
      >
        <a href={cta.url}>{cta.label}</a>
      </Button>
    );
  }
  return (
    <Button
      asChild
      className="bg-onplexus-gold text-onplexus-dark hover:bg-onplexus-gold-bright rounded-xl px-6 py-3 h-12 font-semibold text-lg"
    >
      <Link to={cta.url}>{cta.label}</Link>
    </Button>
  );
}

function FeatureLink({ link }: { link?: HomeCta | null }) {
  const label = link?.label?.trim() || "Learn More";
  const url = link?.url?.trim();
  const content = (
    <a 
      href={url}
        className="inline-flex items-center gap-1 pl-3 pr-2 rounded bg-white/10 text-white text-sm hover:bg-white/25 transition-colors transition-colors w-fit cursor-pointer"
        >
      <span>{label}</span>
      <span className="onplexus-icon text-3xl pt-0.5">↗</span>
    </a>
  );

  if (!url) {
    return (
      <a 
      href={url}
        className="inline-flex items-center gap-1 pl-3 pr-2 rounded bg-white/10 text-white text-sm hover:bg-white/25 transition-colors transition-colors w-fit cursor-pointer"
        >
      <span>{label}</span>
      <span className="onplexus-icon text-3xl pt-0.5">↗</span>
    </a>
    );
  }

  if (isExternalUrl(url)) {
    return (
      <a 
      href={url}
        className="inline-flex items-center gap-1 pl-3 pr-2 rounded bg-white/10 text-white text-sm hover:bg-white/25 transition-colors transition-colors w-fit cursor-pointer" target="_blank" rel="nofollow noopener noreferrer"
        >
      <span>{label}</span>
      <span className="onplexus-icon text-3xl pt-0.5">↗</span>
    </a>
    );
  }

  if (url.startsWith("#")) {
    return (
      <a 
      href={url}
        className="inline-flex items-center gap-1 pl-3 pr-2 rounded bg-white/10 text-white text-sm hover:bg-white/25 transition-colors transition-colors w-fit cursor-pointer"
        >
      <span>{label}</span>
      <span className="onplexus-icon text-3xl pt-0.5">↗</span>
    </a>
    );
  }

  return (
    <Link
      to={url}
      className="flex items-center gap-1 text-white text-sm cursor-pointer hover:text-onplexus-gold transition-colors cursor-pointer"
    >
      {content}
    </Link>
  );
}

export default function Growth({ data }: GrowthProps) {
  const [shouldRender, setShouldRender] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const canAnimate = Boolean(data.featureLottieSrc);

  useEffect(() => {
    if (!containerRef.current || !canAnimate) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setShouldRender(true);
          observer.disconnect();
        }
      },
      { root: null, rootMargin: "0px 0px -20% 0px", threshold: 0.1 }
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [canAnimate]);

  useEffect(() => {
    if (!canAnimate) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    preloadDotLottie();
  }, [canAnimate]);

  useEffect(() => {
    if (!shouldRender || !canAnimate) return;
    let mounted = true;

    loadDotLottie().catch((error) => {
      if (mounted) {
        console.error("Failed to load dotlottie player", error);
      }
    });

    return () => {
      mounted = false;
    };
  }, [shouldRender, canAnimate]);

  return (


    <section className="py-16">
      <div className="flex max-w-6xl mx-auto px-4">
        <div className="tech-div-ver tech-div-ft tech-div-fb md:hidden"></div>
        <div className="flex flex-col w-full md:flex-row gap-0 relative items-stretch tech-benefit-contaioner">

          <div className="tech-div-ver tech-div-ft tech-div-fb hidden md:flex"></div>

          {/* Left Side - Feature Showcase */}
          <div className="flex flex-col grow h-full relative" ref={containerRef}>
            <div className="tech-div-hoz tech-div-fl"></div>
            <div className="relative z-10 p-3 pt-2 pb-10 ">
              {/* Point indicator */}
              <div className="mb-6">
                <span className="text-xs text-white/60 tracking-widest font-normal">
                  {data.pointLabel}
                </span>
              </div>
              {/* Feature Lottie Animation */}
              <div className="mb-6 px-3 flex justify-left">
                {shouldRender && canAnimate ? (
                  <DotLottieCanvas
                    src={data.featureLottieSrc!}
                    width={512}
                    height={512}
                    className="w-full h-auto max-w-[280px] max-h-[280px] sm:max-w-[320px] sm:max-h-[320px] md:max-w-[280px] md:max-h-[280px] lg:max-w-[400px] lg:max-h-[400px]"
                    aria-label={data.featureLottieAlt ?? undefined}
                  />
                ) : (
                  <div className="h-[200px] w-[200px] rounded-full bg-onplexus-gold/10 blur-3xl" />
                )}
              </div>
              {/* Feature details */}
              <div className="space-y-3 px-3">
                <h3 className="text-xl font-bold text-white leading-6">
                  {data.featureTitle}
                </h3>
                <p className="text-sm text-white/70 leading-normal">
                  {data.featureDescription}
                </p>
                <FeatureLink link={data.featureLink} />
              </div>
            </div>
            <div className="tech-div-hoz tech-div-fl"></div>
          </div>
          <div className="tech-div-ver tech-div-ft tech-div-fb hidden md:flex"></div>
          {/* Right Side - Growth Content */}
          <div className="relative flex flex-col grow justify-center relative featuredBlueprint-text">
            <div className="tech-div-hoz hidden tech-div-fr md:flex"></div>
            {/* Main content */}
            <div className="flex flex-col grow justify-center px-6 py-16 sm:px-6 sm:py-12 md:px-8 md:py-12 lg:px-12 lg:py-12 space-y-6">
              <h2 className="text-4xl lg:text-5xl font-normal text-white leading-tight">
                {data.heading}
              </h2>
              <p className="text-xl text-white/70 leading-relaxed">
                {data.paragraph}
              </p>
              <div className="pt-4">
                <CtaButton cta={data.cta} />
              </div>
            </div>
            <div className="tech-div-hoz tech-div-fl md:hidden"></div>
            <div className="tech-div-hoz tech-div-fr hidden md:flex"></div>
          </div>
        </div>
      </div>
    </section>
  );
}
